import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../config/supabase';
import { AuthContext } from '../hooks/useAuthContext';

const sameSession = (a, b) => {
  if (a === b) return true;
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.access_token === b.access_token && a.user?.id === b.user?.id;
};

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(undefined);
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const loadedProfileUserIdRef = useRef(null);
  const userId = session === undefined ? undefined : (session?.user?.id ?? null);

  // Fetch the session once, and subscribe to auth state changes
  useEffect(() => {
    let mounted = true;

    const fetchSession = async () => {
      setIsLoading(true);
      const {
        data: { session: nextSession },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        console.error('Error fetching session:', error);
      }

      if (!mounted) return;
      setSession(nextSession ?? null);
      if (!nextSession?.user) {
        setIsLoading(false);
      }
    };

    fetchSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      // Ignore duplicate auth events (common on browser tab focus / token refresh)
      setSession((prev) => {
        if (sameSession(prev, nextSession)) {
          return prev;
        }
        console.log('Auth state changed:', { event, userId: nextSession?.user?.id ?? null });
        return nextSession;
      });
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Fetch profile only when the signed-in user changes (not on token refresh)
  useEffect(() => {
    if (userId === undefined) return;

    let cancelled = false;

    const fetchProfile = async () => {
      if (!userId) {
        loadedProfileUserIdRef.current = null;
        setProfile(null);
        setIsLoading(false);
        return;
      }

      // Same user already loaded — keep UI mounted (no loading flash / remount)
      if (loadedProfileUserIdRef.current === userId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (cancelled) return;

      loadedProfileUserIdRef.current = userId;
      setProfile(data);
      setIsLoading(false);
    };

    fetchProfile();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return (
    <AuthContext.Provider
      value={{
        session,
        isLoading,
        profile,
        isLoggedIn: Boolean(session?.user),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
